<template>
    <v-container fluid class="pa-4">
        <div class="d-flex align-center mb-4">
            <v-btn icon variant="text" size="small" class="mr-2" @click="$router.push('/business-dashboard')">
                <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon color="blue" size="28" class="mr-3">mdi-trending-up</v-icon>
            <div>
                <h1 class="text-h5 font-weight-bold">Growth & Signups</h1>
                <p class="text-caption text-medium-emphasis mb-0">Company acquisition, device activations, and market analysis</p>
            </div>
            <v-spacer/>
            <v-select v-model="months" :items="periodOptions" item-title="label" item-value="value"
                      density="compact" variant="outlined" hide-details style="max-width: 160px" class="mr-2"/>
            <v-btn variant="text" :loading="loading" @click="loadData" prepend-icon="mdi-refresh">Refresh</v-btn>
        </div>

        <v-overlay :model-value="loading && !data" contained class="align-center justify-center">
            <v-progress-circular indeterminate size="48"/>
        </v-overlay>

        <v-alert v-if="error" type="error" variant="tonal" closable class="mb-4" @click:close="error = null">
            {{ error }}
            <template v-slot:append>
                <v-btn variant="text" size="small" @click="loadData">Retry</v-btn>
            </template>
        </v-alert>

        <template v-if="data">
            <!-- Company Signups & Device Activations Trend -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="blue" size="20" class="mr-2">mdi-domain</v-icon>
                            New Company Signups
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="signupChartData" :options="barOptions" style="height: 280px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="teal" size="20" class="mr-2">mdi-router-wireless</v-icon>
                            Device Activations
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="activationChartData" :options="simpleBarOptions" style="height: 280px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Cumulative Growth & User Registrations -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="purple" size="20" class="mr-2">mdi-chart-line</v-icon>
                            Cumulative Company Growth
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Line :data="cumulativeChartData" :options="lineOptions" style="height: 280px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="indigo" size="20" class="mr-2">mdi-account-multiple-plus</v-icon>
                            User Registrations
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="userRegChartData" :options="simpleBarOptions" style="height: 280px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Signup Sources, Geography, Industry, Company Size -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3 text-subtitle-2">
                            <v-icon color="orange" size="18" class="mr-2">mdi-cellphone-link</v-icon>
                            Signup Source
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Doughnut v-if="data.signup_sources.length > 0"
                                      :data="sourceChartData" :options="doughnutOptions" style="height: 200px"/>
                            <div v-else class="text-center text-medium-emphasis py-4">No source data</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3 text-subtitle-2">
                            <v-icon color="green" size="18" class="mr-2">mdi-earth</v-icon>
                            Top Countries
                        </v-card-title>
                        <v-divider/>
                        <v-card-text class="pa-0">
                            <v-list density="compact" class="py-0">
                                <v-list-item v-for="geo in data.signup_geo.slice(0, 8)" :key="geo.geo_country_code" class="px-3">
                                    <template #prepend>
                                        <span class="text-body-2 mr-2">{{ countryFlag(geo.geo_country_code) }}</span>
                                    </template>
                                    <v-list-item-title class="text-body-2">{{ geo.geo_country }}</v-list-item-title>
                                    <template #append>
                                        <v-chip size="x-small" color="blue" variant="tonal">{{ geo.count }}</v-chip>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="data.signup_geo.length === 0">
                                    <v-list-item-title class="text-medium-emphasis text-caption">No geo data</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3 text-subtitle-2">
                            <v-icon color="indigo" size="18" class="mr-2">mdi-factory</v-icon>
                            Industries
                        </v-card-title>
                        <v-divider/>
                        <v-card-text class="pa-0">
                            <v-list density="compact" class="py-0">
                                <v-list-item v-for="ind in data.industries.slice(0, 8)" :key="ind.industry" class="px-3">
                                    <v-list-item-title class="text-body-2">{{ ind.industry }}</v-list-item-title>
                                    <template #append>
                                        <v-chip size="x-small" color="purple" variant="tonal">{{ ind.count }}</v-chip>
                                    </template>
                                </v-list-item>
                                <v-list-item v-if="data.industries.length === 0">
                                    <v-list-item-title class="text-medium-emphasis text-caption">No industry data</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3 text-subtitle-2">
                            <v-icon color="teal" size="18" class="mr-2">mdi-chart-bar</v-icon>
                            Company Size (Devices)
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="sizeChartData" :options="horizontalBarOptions" style="height: 200px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {send} from '@/composables/apiClient';
import {Line, Bar, Doughnut} from 'vue-chartjs';
import {Chart, registerables} from 'chart.js';

Chart.register(...registerables);

const loading = ref(false);
const data = ref(null);
const error = ref(null);
const months = ref(12);
const periodOptions = [
    {label: 'Last 6 months', value: 6},
    {label: 'Last 12 months', value: 12},
    {label: 'Last 24 months', value: 24},
    {label: 'Last 36 months', value: 36},
];

function monthLabel(year, month) {
    return new Date(year, month - 1).toLocaleDateString('en-US', {month: 'short', year: '2-digit'});
}

function countryFlag(countryCode) {
    if (!countryCode || countryCode.length !== 2) return '';
    return String.fromCodePoint(...[...countryCode.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

const signupChartData = computed(() => {
    if (!data.value?.company_signups) return {labels: [], datasets: [{data: []}]};
    const s = data.value.company_signups;
    return {
        labels: s.map(r => monthLabel(r.year, r.month)),
        datasets: [
            {label: 'Approved', data: s.map(r => r.approved), backgroundColor: '#4CAF50', stack: 'a'},
            {label: 'Pending', data: s.map(r => r.pending), backgroundColor: '#FF9800', stack: 'a'},
            {label: 'Rejected', data: s.map(r => r.rejected), backgroundColor: '#F44336', stack: 'a'},
        ],
    };
});

const activationChartData = computed(() => {
    if (!data.value?.device_activations) return {labels: [], datasets: [{data: []}]};
    const a = data.value.device_activations;
    return {
        labels: a.map(r => monthLabel(r.year, r.month)),
        datasets: [{
            label: 'Activations',
            data: a.map(r => r.total),
            backgroundColor: '#009688',
        }],
    };
});

const cumulativeChartData = computed(() => {
    if (!data.value?.cumulative_companies) return {labels: [], datasets: [{data: []}]};
    const c = data.value.cumulative_companies;
    return {
        labels: c.map(r => monthLabel(r.year, r.month)),
        datasets: [{
            label: 'Total Companies',
            data: c.map(r => r.cumulative),
            borderColor: '#9C27B0',
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
            fill: true,
            tension: 0.3,
        }],
    };
});

const userRegChartData = computed(() => {
    if (!data.value?.user_registrations) return {labels: [], datasets: [{data: []}]};
    const u = data.value.user_registrations;
    return {
        labels: u.map(r => monthLabel(r.year, r.month)),
        datasets: [{
            label: 'New Users',
            data: u.map(r => r.company_users),
            backgroundColor: '#3F51B5',
        }],
    };
});

const sourceChartData = computed(() => {
    if (!data.value?.signup_sources) return {labels: [], datasets: [{data: []}]};
    const s = data.value.signup_sources;
    const sourceLabels = {web: 'Web Portal', app: 'Mobile App', unknown: 'Unknown'};
    return {
        labels: s.map(r => sourceLabels[r.client_source] || r.client_source),
        datasets: [{
            data: s.map(r => r.count),
            backgroundColor: ['#2196F3', '#4CAF50', '#9E9E9E', '#FF9800'],
        }],
    };
});

const sizeChartData = computed(() => {
    if (!data.value?.company_size_distribution) return {labels: [], datasets: [{data: []}]};
    const d = data.value.company_size_distribution;
    return {
        labels: Object.keys(d),
        datasets: [{
            label: 'Companies',
            data: Object.values(d),
            backgroundColor: ['#EF5350', '#FF9800', '#FFC107', '#4CAF50', '#2196F3', '#9C27B0'],
        }],
    };
});

const barOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {position: 'top'}},
    scales: {x: {stacked: true}, y: {stacked: true, beginAtZero: true}},
};

const simpleBarOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {display: false}},
    scales: {y: {beginAtZero: true}},
};

const lineOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {display: false}},
    scales: {y: {beginAtZero: true}},
};

const doughnutOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {position: 'bottom'}},
};

const horizontalBarOptions = {
    responsive: true, maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {legend: {display: false}},
    scales: {x: {beginAtZero: true}},
};

async function loadData() {
    loading.value = true;
    error.value = null;
    try {
        const response = await send('get', 'admin/reports/growth', {months: months.value});
        if (response?.data?.data) {
            data.value = response.data.data;
        } else {
            error.value = response?.data?.message || 'Failed to load data';
        }
    } catch (e) {
        console.error('Failed to load growth data', e);
        error.value = e?.response?.data?.message || e?.message || 'Failed to load data';
    } finally {
        loading.value = false;
    }
}

watch(months, () => loadData());
onMounted(loadData);
</script>
